import React, { useState } from 'react';

const PANE_WIDTH_STORAGE_KEY = 'cm-results-pane-width';
const DEFAULT_PANE_WIDTH = 360;
const MIN_PANE_WIDTH = 220;
const MIN_GRAPH_WIDTH = 320;
const PANE_KEYBOARD_STEP = 24;

const loadPaneWidth = (): number => {
    try {
        const stored = Number(window.localStorage.getItem(PANE_WIDTH_STORAGE_KEY));
        return stored >= MIN_PANE_WIDTH ? stored : DEFAULT_PANE_WIDTH;
    } catch {
        return DEFAULT_PANE_WIDTH;
    }
};

const storePaneWidth = (width: number) => {
    try {
        window.localStorage.setItem(PANE_WIDTH_STORAGE_KEY, `${Math.round(width)}`);
    } catch {
        // Remembering the width is only a convenience.
    }
};

// Width of the side pane next to the graph, resized by dragging the splitter
// between them or with the arrow keys on it. The width is shared by every
// page with a side pane and remembered across visits.
export const useResizablePane = () => {
    const [paneWidth, setPaneWidth] = useState(loadPaneWidth);
    const [isResizingPane, setIsResizingPane] = useState(false);
    const bodyRef = React.useRef<HTMLDivElement>(null);

    // Keep the pane between its minimum width and leaving the graph its minimum.
    const clampPaneWidth = (width: number): number => {
        const bodyWidth = bodyRef.current?.getBoundingClientRect().width ?? Infinity;
        const maxWidth = Math.max(MIN_PANE_WIDTH, bodyWidth - MIN_GRAPH_WIDTH);
        return Math.min(Math.max(width, MIN_PANE_WIDTH), maxWidth);
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsResizingPane(true);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const body = bodyRef.current;

        if (!isResizingPane || !body) {
            return;
        }

        setPaneWidth(clampPaneWidth(body.getBoundingClientRect().right - event.clientX));
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isResizingPane) {
            return;
        }

        event.currentTarget.releasePointerCapture(event.pointerId);
        setIsResizingPane(false);
        storePaneWidth(paneWidth);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const delta = event.key === 'ArrowLeft' ? PANE_KEYBOARD_STEP
            : event.key === 'ArrowRight' ? -PANE_KEYBOARD_STEP
                : 0;

        if (delta === 0) {
            return;
        }

        event.preventDefault();
        const nextWidth = clampPaneWidth(paneWidth + delta);
        setPaneWidth(nextWidth);
        storePaneWidth(nextWidth);
    };

    return {
        paneWidth,
        isResizingPane,
        bodyRef,
        // Spread onto the splitter element.
        splitterProps: {
            role: 'separator',
            'aria-orientation': 'vertical' as const,
            'aria-valuenow': Math.round(paneWidth),
            tabIndex: 0,
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerUp,
            onPointerCancel: handlePointerUp,
            onKeyDown: handleKeyDown,
        },
    };
};
